import { useState, useEffect, useRef } from "react";
import { Modal, Table, DatePicker, InputNumber, Select, Button } from "antd";
import dayjs from "dayjs";
import { withLocale } from "../../l10n";
import {
  STATE_ACTIVE,
  STATE_CANCELLED,
  STATE_EXPIRED,
  STATE_INACTIVE,
  STATE_NOT_APPLICABLE
} from "../../utils/constants";

function EditRenewalModal({
  open,
  loading,
  rows,
  onSubmit,
  onCancel,
  getRowKey,
  lang
}) {

  const [editedRows, setEditedRows] = useState({});

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Inicializa el "borrador" cada vez que se abre el modal; lo descarta al cerrar.
  useEffect(() => {
    if (!open) {
      setEditedRows({});
      return;
    }

    const initialEdits = {};
    rowsRef.current.forEach(row => {
      const rowKey = getRowKey(row);
      const editable = {
        renewal_date: dayjs(row.renewal_date).format('DD-MM-YYYY'),
        renewal_interval: row.renewal_interval,
        subscription_state: row.ki_subscription_state,
      };
      initialEdits[rowKey] = {
        uuid: row.uuid,
        subscription_type: row.subscription_type,
        ...editable,
        _original: editable,
      };
    });
    setEditedRows(initialEdits);
  }, [open, getRowKey]);

  const handleFieldChange = (rowKey, field, value) => {
    setEditedRows(prev => {
      const updatedRow = { ...prev[rowKey], [field]: value };
      if (field === 'subscription_state' && value === STATE_NOT_APPLICABLE) {
        updatedRow.renewal_interval = 0;
      }
      return { ...prev, [rowKey]: updatedRow };
    });
  };

  const handleResetRow = (rowKey) => {
    setEditedRows(prev => {
      const current = prev[rowKey];
      return {
        ...prev,
        [rowKey]: { ...current, ...current._original }
      };
    });
  };

  const hasChanges = (rowKey) => {
    const data = editedRows[rowKey];
    if (!data || !data._original) return false;
    const original = data._original;
    return (
      data.renewal_date !== original.renewal_date ||
      data.renewal_interval !== original.renewal_interval ||
      data.subscription_state !== original.subscription_state
    );
  };

  const title = rows.length === 1
    ? `${lang.handle('Editando')} 1 ${lang.handle('suscripción')}`
    : `${lang.handle('Editando')} ${rows.length} ${lang.handle('suscripciones')}`;

  
  const validateRow = (rowKey) => {
    const data = editedRows[rowKey];
    if (!data) return true;
    const state = data.subscription_state;
    const interval = data.renewal_interval;
    if ((state === STATE_ACTIVE || state === STATE_INACTIVE) && (!interval || interval <= 0)) {
      return false;
    }
    return true;
  };

  const hasValidationErrors = Object.keys(editedRows).some(uuid => !validateRow(uuid));
  const hasAnyChanges = Object.keys(editedRows).some(uuid => hasChanges(uuid));

  const columns = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      width: '25%',
      render: (uuid) => (
        <div className="modal-cell-content">
          <span className="modal-cell-text">{uuid}</span>
        </div>
      )
    },
    {
      title: 'Organizacion',
      dataIndex: 'tenant_name',
      width: '15%',
      render: (tenant_name) => (
        <div className="modal-cell-content">
          <span className="modal-cell-text">{tenant_name}</span>
        </div>
      )
    },
    {
      title: lang.handle('Fecha de renovación'),
      dataIndex: 'renewal_date',
      width: '20%',
      render: (_, record) => {
        const rowKey = getRowKey(record);
        const data = editedRows[rowKey];
        if (!data) return null;
        const state = data.subscription_state;
        const isDisabled = state === STATE_CANCELLED || state === STATE_NOT_APPLICABLE || state === STATE_EXPIRED;
        return (
          <DatePicker
            size="small"
            format="DD-MM-YYYY"
            value={dayjs(data.renewal_date, 'DD-MM-YYYY')}
            onChange={(date, dateString) =>
              handleFieldChange(rowKey, 'renewal_date', dateString)
            }
            className="modal-input-full-width"
            disabled={isDisabled}
            allowClear={false}
          />
        );
      }
    },
    {
      title: (
        <span>
          {lang.handle('Intervalo de renovación')}
          <span className="modal-interval-label">
            {' '}({lang.handle('meses')})
          </span>
        </span>
      ),
      dataIndex: 'renewal_interval',
      width: '20%',
      render: (_, record) => {
        const rowKey = getRowKey(record);
        const state = editedRows[rowKey]?.subscription_state;
        const interval = editedRows[rowKey]?.renewal_interval;
        const isDisabled = state === STATE_CANCELLED || state === STATE_NOT_APPLICABLE || state === STATE_EXPIRED;
        const isInvalid = !validateRow(rowKey);

        return (
          <div>
            <InputNumber
              size="small"
              min={0}
              max={120}
              value={interval}
              onChange={(value) =>
                handleFieldChange(rowKey, 'renewal_interval', value)
              }
              className="modal-input-full-width"
              disabled={isDisabled}
              status={isInvalid ? "error" : ""}
            />
          </div>
        );
      }
    },
    {
      title: lang.handle('Estado'),
      dataIndex: 'subscription_state',
      width: '25%',
      render: (_, record) => {
        const rowKey = getRowKey(record);
        return (
          <Select
            size="small"
            value={editedRows[rowKey]?.subscription_state}
            onChange={(value) =>
              handleFieldChange(rowKey, 'subscription_state', value)
            }
            className="modal-input-full-width"
            options={[
              { label: lang.handle('Activo'), value: STATE_ACTIVE },
              { label: lang.handle('Lista para activación'), value: STATE_INACTIVE },
              { label: lang.handle('Cancelado'), value: STATE_CANCELLED },
              { label: lang.handle('No procede'), value: STATE_NOT_APPLICABLE }
            ]}
          />
        );
      }
    },
    {
      title: '',
      key: 'actions',
      width: '5%',
      render: (_, record) => {
        const rowKey = getRowKey(record);
        return hasChanges(rowKey) ? (
          <Button
            type="link"
            size="small"
            onClick={() => handleResetRow(rowKey)}
            className="modal-reset-button"
          >
            {lang.handle('Reset')}
          </Button>
        ) : null;
      }
    }
  ];

  const handleSubmit = () => {
  const changedRows = Object.entries(editedRows)
      .filter(([rowKey]) => hasChanges(rowKey))
      .map(([, data]) => data);
    onSubmit(changedRows);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      cancelText={lang.handle('Cerrar')}
      okText={lang.handle('Guardar')}
      confirmLoading={loading}
      width={900}
      maskClosable={false}
      okButtonProps={{ disabled: hasValidationErrors || !hasAnyChanges }}
    >
      <div className="edit-renewals-container">
        <Table
          size="small"
          pagination={false}
          dataSource={rows}
          rowKey={getRowKey}
          rowClassName={(record) => hasChanges(getRowKey(record)) ? 'row-modified' : ''}
          columns={columns}
        />
      </div>
    </Modal>
  );
}

export default withLocale(EditRenewalModal);
